/**
 * Chat do assistente — componente único do CCO e do SCADA 3D — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 26/10/2026 · Versão: 1.4.0
 *
 * Pedido do responsável do produto (17/10/2026): "fazer o chat do SCADA no mesmo padrão e trazer o agente no topo, para
 * termos um padrão". Mesmo componente nas duas telas, sempre no topo do painel:
 * - pergunta → `POST /api/v1/assistente/perguntar` (roteador em Python: perguntas padrão sem LLM; análise pelo agente);
 * - selo da origem ("consulta direta" sem custo ou "agente IA" com custo), duração e sugestões;
 * - no SCADA 3D vai também o setor e a situação calculada pela tela (escopo do setor);
 * - nunca manobra: o backend recusa.
 *
 * US-3201: conversa com memória — a pergunta leva o `conversa_id` da resposta anterior ("e ontem?" funciona); "Nova
 * conversa" começa outra; com `comConversas` (gaveta do CCO), a lista das conversas do usuário com busca reabre uma.
 * US-3202: a pergunta vai por `/perguntar/stream` (SSE pelo backend): a mensagem do assistente mostra o que o gestor
 * está consultando ("comparando períodos da pressão do N653…") e a resposta é escrita aos poucos.
 * US-3203: respostas ricas do agente — referências clicáveis (cada número abre o gráfico no CCO), confiança, avisos,
 * fontes e os botões "Abrir … no CCO" e "Comparar com a semana passada". Nenhum botão de manobra.
 * US-3204: 📎 anexa foto, PDF ou planilha (até 3; tipo e tamanho conferidos no servidor) à próxima pergunta.
 */
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ErroApi, fluxo } from "./api";
import { podeAcessarRota } from "./acessoRotas";
import { useSessao } from "./sessao";

type Origem = "consulta" | "agente" | "indisponivel";
type Referencia = { ponto: string; medida: string; valor: string; rota: string };
type Acao = { tipo: "abrir" | "perguntar"; rotulo: string; rota?: string; pergunta?: string };
type Resposta = { texto: string; origem: Origem; intencao: string; custo_usd: number; duracao_ms: number; avisos?: string[]; confianca?: string;
  conversa_id?: string; referencias?: Referencia[]; acoes?: Acao[]; fontes?: string[] };
const CONFIANCA: Record<string, string> = { alta: "confiança alta", media: "confiança média", baixa: "confiança baixa" };
type ConversaResumo = { id: string; titulo: string; canal: string; atualizada_em: string; mensagens: number };
type ConversaAberta = { id: string; mensagens: { papel: "usuario" | "assistente"; texto: string; origem: string }[] };
type Mensagem = { de: "usuario" | "assistente"; texto: string; origem?: Origem; detalhe?: string; status?: string;
  confianca?: string; avisos?: string[]; referencias?: Referencia[]; acoes?: Acao[]; fontes?: string[] };

const SELO: Record<Origem, string> = { consulta: "consulta direta", agente: "agente IA", indisponivel: "agente indisponível" };

export function ChatAssistente({ empresa, titulo, subtitulo, setor, situacao, sugestoes, saudacao, topoExtra, placeholder, rotuloEntrada, comConversas }: {
  empresa: string | null; titulo: string; subtitulo?: string; setor?: string; situacao?: () => Record<string, unknown>;
  sugestoes: string[]; saudacao?: string; topoExtra?: ReactNode; placeholder?: string; rotuloEntrada?: string; comConversas?: boolean;
}) {
  const { usuario } = useSessao();
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [conversaId, setConversaId] = useState<string | null>(null);
  const [lista, setLista] = useState<ConversaResumo[] | null>(null);
  const [busca, setBusca] = useState("");
  const [anexos, setAnexos] = useState<{ id: string; nome: string; lido: boolean }[]>([]);
  const [enviandoAnexo, setEnviandoAnexo] = useState<string | null>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const anexar = async (arquivo: File | undefined) => {
    if (!arquivo || !empresa) return;
    setEnviandoAnexo(`enviando ${arquivo.name}…`);
    const form = new FormData();
    form.append("arquivo", arquivo);
    form.append("empresa", empresa);
    if (conversaId) form.append("conversa_id", conversaId);
    try {
      const a = await api<{ id: string; nome: string; lido: boolean }>("/v1/assistente/anexos", { form });
      setAnexos((l) => [...l, a].slice(0, 3));
      setEnviandoAnexo(null);
    } catch (e) {
      setEnviandoAnexo(e instanceof ErroApi ? `Arquivo recusado: ${e.message}` : "Falha ao enviar o arquivo.");
    } finally {
      if (seletor.current) seletor.current.value = "";
    }
  };
  const [texto, setTexto] = useState("");
  const [esperando, setEsperando] = useState(false);
  const fim = useRef<HTMLDivElement>(null);
  const novaConversa = () => { setConversaId(null); setMensagens(saudacao ? [{ de: "assistente", texto: saudacao }] : []); };
  useEffect(novaConversa, [setor, saudacao]);
  const carregarLista = (termo = busca) => api<ConversaResumo[]>(`/v1/assistente/conversas${termo.trim() ? `?busca=${encodeURIComponent(termo.trim())}` : ""}`)
    .then(setLista).catch(() => setLista([]));
  const abrirConversa = async (id: string) => {
    const c = await api<ConversaAberta>(`/v1/assistente/conversas/${id}`).catch(() => null);
    if (!c) return;
    setConversaId(c.id);
    setMensagens(c.mensagens.map((m) => ({ de: m.papel, texto: m.texto, origem: (m.origem || undefined) as Origem | undefined })));
    setLista(null);
  };
  useEffect(() => { fim.current?.scrollIntoView({ block: "nearest" }); }, [mensagens, esperando]);

  const perguntar = async (pergunta: string) => {
    if (!empresa || !pergunta.trim() || esperando) return;
    const ids = anexos.map((a) => a.id);
    setMensagens((m) => [...m, { de: "usuario", texto: pergunta + anexos.map((a) => `\n📎 ${a.nome}`).join("") }]);
    setAnexos([]);
    setTexto("");
    setEsperando(true);
    // a última mensagem é a do assistente em construção (status e texto chegando pelo fluxo)
    const atualizar = (f: (m: Mensagem) => Mensagem) => setMensagens((ms) => [...ms.slice(0, -1), f(ms[ms.length - 1])]);
    setMensagens((m) => [...m, { de: "assistente", texto: "", status: "consultando…" }]);
    try {
      let final: Resposta | null = null;
      let erro: string | null = null;
      await fluxo("/v1/assistente/perguntar/stream",
        { empresa, texto: pergunta, ...(conversaId ? { conversa_id: conversaId } : {}), ...(ids.length ? { anexos: ids } : {}),
          ...(setor ? { setor, situacao: situacao?.() } : {}) },
        (evento, dados) => {
          if (evento === "status") atualizar((m) => ({ ...m, status: String(dados.texto ?? "consultando…") }));
          else if (evento === "texto") atualizar((m) => ({ ...m, status: undefined, texto: m.texto + String(dados.pedaco ?? "") }));
          else if (evento === "fim") final = dados as Resposta;
          else if (evento === "erro") erro = String(dados.detalhe ?? "erro");
        });
      if (erro || !final) throw new ErroApi(500, erro ?? "a resposta não chegou");
      const r: Resposta = final;
      if (r.conversa_id) setConversaId(r.conversa_id);
      const detalhe = [`${r.duracao_ms} ms`, r.origem === "agente" ? `US$ ${r.custo_usd.toFixed(4)}` : "sem custo"].join(" · ");
      atualizar(() => ({ de: "assistente", texto: r.texto, origem: r.origem, detalhe, confianca: r.confianca, avisos: r.avisos,
        referencias: r.referencias, acoes: r.acoes, fontes: r.fontes }));
    } catch (e) {
      atualizar(() => ({ de: "assistente", origem: "indisponivel",
        texto: e instanceof ErroApi ? `Não consegui responder: ${e.message}` : "Falha de rede." }));
    } finally {
      setEsperando(false);
    }
  };
  const enviar = (e: FormEvent) => { e.preventDefault(); void perguntar(texto); };
  const soSaudacao = mensagens.length <= (saudacao ? 1 : 0);

  return (
    <section className="chat-assistente" aria-label={titulo}>
      <header className="topo-chat">
        <h3>{titulo}</h3>
        {subtitulo && <span className="suave nx-suave">{subtitulo}</span>}
        <span className="espaco" />
        {topoExtra}
        {conversaId && <button type="button" className="nx-botao" onClick={novaConversa}>Nova conversa</button>}
        {comConversas && <button type="button" className="nx-botao" aria-expanded={lista !== null}
          onClick={() => (lista ? setLista(null) : void carregarLista())}>Conversas</button>}
      </header>
      {lista && (
        <div className="lista-conversas" role="region" aria-label="Minhas conversas">
          <input type="search" value={busca} placeholder="buscar nas conversas" aria-label="Buscar nas conversas"
            onChange={(e) => { setBusca(e.target.value); void carregarLista(e.target.value); }} />
          {lista.map((c) => (
            <button key={c.id} type="button" className={`nx-botao item-conversa${c.id === conversaId ? " ativo" : ""}`} onClick={() => void abrirConversa(c.id)}>
              {c.titulo} <small className="nx-suave">{c.canal !== "tela" ? `${c.canal} · ` : ""}{new Date(c.atualizada_em).toLocaleString("pt-BR")} · {c.mensagens} msg</small>
            </button>
          ))}
          {!lista.length && <p className="nx-suave">nenhuma conversa{busca ? " com esse termo" : ""}</p>}
        </div>
      )}
      <form onSubmit={enviar} className="pergunta">
        <input value={texto} onChange={(e) => setTexto(e.target.value)} maxLength={1000} aria-label={rotuloEntrada ?? `Pergunta: ${titulo}`}
          placeholder={placeholder ?? "ex.: nível do reservatório RAP05"} disabled={!empresa} />
        <input ref={seletor} type="file" hidden accept=".jpg,.jpeg,.png,.webp,.pdf,.xlsx,.csv" aria-label="Arquivo para anexar"
          onChange={(e) => void anexar(e.target.files?.[0])} />
        <button type="button" className="nx-botao" title="Anexar foto, PDF ou planilha" disabled={!empresa || anexos.length >= 3}
          onClick={() => seletor.current?.click()}>📎</button>
        <button type="submit" className="nx-botao nx-botao-principal" disabled={esperando || !texto.trim()}>Perguntar</button>
      </form>
      {(anexos.length > 0 || enviandoAnexo) && (
        <div className="anexos-pendentes" aria-label="Anexos da próxima pergunta">
          {anexos.map((a) => (
            <span key={a.id} className="nx-selo">📎 {a.nome}{a.lido ? "" : " (sem texto legível)"}
              <button type="button" className="nx-botao" aria-label={`Tirar ${a.nome}`} onClick={() => setAnexos((l) => l.filter((x) => x.id !== a.id))}>×</button></span>
          ))}
          {enviandoAnexo && <span className="nx-suave" role="status">{enviandoAnexo}</span>}
        </div>
      )}
      {(soSaudacao || mensagens.length > 0) && (
        <div className="conversa" aria-live="polite">
          {mensagens.map((m, i) => {
            const referencias = m.referencias?.filter((r) => usuario && podeAcessarRota(usuario, r.rota)) ?? [];
            const acoes = m.acoes?.filter((a) => a.tipo !== "abrir" || (a.rota && usuario && podeAcessarRota(usuario, a.rota))) ?? [];
            return (
            <div key={i} className={`mensagem ${m.de}`}>
              {m.status && <p className="status-agente nx-suave" role="status">🔎 {m.status}</p>}
              {(m.texto || !m.status) && <p>{m.texto}</p>}
              {referencias.length > 0 && (
                <div className="referencias" aria-label="De onde vêm os números">
                  {referencias.map((r, j) => (
                    <Link key={j} to={r.rota} className="nx-selo referencia" title={`${r.ponto} · ${r.medida} — abrir o gráfico`}>
                      📈 {r.valor ? `${r.valor} · ` : ""}{r.ponto.toUpperCase()} · {r.medida.replace(/_/g, " ")}</Link>
                  ))}
                </div>
              )}
              {m.avisos && m.avisos.length > 0 && <ul className="avisos-resposta nx-suave">{m.avisos.map((a, j) => <li key={j}>⚠ {a}</li>)}</ul>}
              {acoes.length > 0 && (
                <div className="acoes-resposta">
                  {acoes.map((a, j) => a.tipo === "abrir" && a.rota
                    ? <Link key={j} to={a.rota} className="nx-botao">{a.rotulo}</Link>
                    : <button key={j} type="button" className="nx-botao" onClick={() => a.pergunta && void perguntar(a.pergunta)}>{a.rotulo}</button>)}
                </div>
              )}
              {m.origem && <small><span className={`nx-selo selo-${m.origem}`}>{SELO[m.origem]}</span>
                {m.confianca && <span className={`nx-selo confianca-${m.confianca}`}>{CONFIANCA[m.confianca] ?? m.confianca}</span>} {m.detalhe}
                {m.fontes && m.fontes.length > 0 && <details className="fontes-resposta"><summary>fontes</summary><ul>{m.fontes.map((f, j) => <li key={j}>{f}</li>)}</ul></details>}
              </small>}
            </div>
            );
          })}
          {soSaudacao && (
            <div className="sugestoes">{sugestoes.map((s) => <button key={s} type="button" className="nx-botao" onClick={() => void perguntar(s)}>{s}</button>)}</div>
          )}
          <div ref={fim} />
        </div>
      )}
    </section>
  );
}
